import supabase, { SupabaseDatabase } from "@/features/supabase";
import { ToolsPermissions } from "@/store/services/supabase/types";
import { getSupabaseError, getSupabaseFunctionError } from "@/store/services/supabase/utils";
import { ANALYSIS_PAGE_SIZE } from "@/tools/aggregate/portfolio-export/constants";
import { AdaptationType } from "@/types/adaptation";
import { arrayBufferToHex } from "@/utils/crypto";
import { getUTCDate } from "@/utils/datetime";
import { createApi, fakeBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import {
  AuthMFAGetAuthenticatorAssuranceLevelResponse,
  AuthOtpResponse,
  AuthResponse,
  EmailOtpType,
} from "@supabase/supabase-js";

type NonNullableSpecific<T, K extends keyof T> = Omit<T, K> & {
  [P in K]-?: Exclude<T[P], null>;
};

export type User = SupabaseDatabase["public"]["Views"]["v_users"]["Row"];
export type Org = SupabaseDatabase["public"]["Tables"]["orgs"]["Row"];
type UserGroup = SupabaseDatabase["public"]["Tables"]["users_groups"]["Row"];
type Group = SupabaseDatabase["public"]["Tables"]["groups"]["Row"];
type Portfolio = SupabaseDatabase["public"]["Tables"]["portfolios"]["Row"];
export type PortfolioAsset = SupabaseDatabase["public"]["Tables"]["portfolio_assets"]["Row"];
export type PortfolioView = NonNullableSpecific<
  SupabaseDatabase["public"]["Views"]["v_portfolios"]["Row"],
  "pending" | "error" | "success" | "total" | "unprocessed"
>;
export type LargeSiteView = NonNullableSpecific<
  SupabaseDatabase["public"]["Views"]["v_large_sites"]["Row"],
  "pending" | "error" | "success" | "total" | "unprocessed"
>;
type ToolQuota = SupabaseDatabase["public"]["Tables"]["tool_quotas"]["Row"];
type Rpc = SupabaseDatabase["public"]["Functions"];

export type GetTpuSubunitRequest = { [id: string]: [lat: number, lng: number] };

export type GetTpuSubunitResponse = {
  [id: string]: {
    tpu: string;
    subunit: string;
  };
};

export type GetGroup = Omit<Group, "permissions"> & {
  total_members: number;
  tools_enabled: string[];
  permissions: ToolsPermissions;
};

export type ToolLimit = {
  percent: number;
  absolute: number;
  hard_limit_percent: number;
};

export enum QuotaAllowanceType {
  Yearly = "yearly",
  OneOff = "one-off",
}

export type ToolQuotaWithActivePeriod = {
  quota: number;
  created_at: Date | null;
  expires_at: Date | null;
  allowance_type: QuotaAllowanceType;
};

type GetToolUsageRpcResponse = {
  quota: number; // Deprecated: Portfolio Export's quota. Use quotas object instead.
  quotas: {
    largeSite: ToolQuotaWithActivePeriod;
    portfolioInsights: ToolQuotaWithActivePeriod;
    portfolioInsightsGlobal: ToolQuotaWithActivePeriod;
    screening: ToolQuotaWithActivePeriod;
  };
  usage: {
    total: number;
    screening: number;
    portfolioInsights: number;
    portfolioInsightsGlobal: number;
    largeSite: number;
  };
  buffers: {
    largeSite: ToolLimit;
    portfolioInsights: ToolLimit;
    portfolioInsightsGlobal: ToolLimit;
    screening: ToolLimit;
  };
  singleQuotaMode: boolean;
  portfolioExportHardLimitPercent: number;
};

export type CurrentToolUsage = {
  alwaysEnabled: boolean;
  enabled: boolean;
  showWarning: "none" | "warn" | "error";
  quota: number;
  usage: number;
  usagePercent: number;
  hardLimitPercent: number;
};

export type GetToolUsageResponse = GetToolUsageRpcResponse & {
  portfolioInsights: CurrentToolUsage;
  portfolioInsightsGlobal: CurrentToolUsage;
  largeSite: CurrentToolUsage;
  screening: CurrentToolUsage;
  singleQuotaUsage: CurrentToolUsage | null;
};

export type AdaptationPathwayAction = SupabaseDatabase["public"]["Tables"]["adaptation_pathway_actions"]["Row"];
export type GetPointAdaptationResponse = SupabaseDatabase["public"]["Tables"]["adaptation_pathways"]["Row"] & {
  adaptation_pathway_actions: AdaptationPathwayAction[];
};

export type AdaptationAnalysis = {
  portfolio_id: string;
  portfolio_asset_id: string;
  adaptation_type: AdaptationType;
};

export type GetAdaptationActionResponse = AdaptationPathwayAction & AdaptationAnalysis;

export type EncryptedAnalysisWithPointId = {
  supabaseId: string;
  id: string;
  data: string | null;
};

const tagTypes = [
  "User",
  "Domain",
  "Group",
  "Slug",
  "Org",
  "Portfolio",
  "PortfolioAssets",
  "Keys",
  "ActionLogs",
  "Adaptations",
  "AdaptationPathways",
  "AdaptationPathwayActions",
  "ReportImages",
  "TemplateVersions",
  "VersionHistory",
  "Templates",
  "Dataset",
];

export const supabaseApi = createApi({
  reducerPath: "users",
  baseQuery: fakeBaseQuery<FetchBaseQueryError>(),
  tagTypes,
  endpoints: (builder) => ({
    getCurrentUser: builder.query<User, void>({
      providesTags: [{ type: "User", id: "CURRENT" }],
      queryFn: async () => {
        const session = await supabase.auth.getSession();
        if (!session.data.session || session.error) {
          return {
            error: getSupabaseError(session.error || { message: "Session not found" }),
          };
        }

        const { data, error } = await supabase
          .from("v_users")
          .select("*")
          .filter("id", "eq", session.data.session.user.id)
          .maybeSingle();

        if (!data) {
          return { error: getSupabaseError({ message: "Couldn't find current user" }) };
        }

        return data
          ? { data: data as User }
          : { error: getSupabaseError(error || { message: "Error finding current user" }) };
      },
    }),

    getUsers: builder.query<User[], void>({
      providesTags: [{ type: "User", id: "LIST" }],
      queryFn: async () => {
        const { data, error } = await supabase.from("v_users").select("*").order("created_at", { ascending: false });

        return data ? { data: data as User[] } : { error: getSupabaseError(error) };
      },
    }),

    getPendingUserCount: builder.query<number, void>({
      providesTags: [{ type: "User", id: "PENDING_COuNT" }],
      queryFn: async () => {
        const { count, error } = await supabase
          .from("v_users")
          .select("*", { count: "exact", head: true })
          .filter("has_user_keys", "eq", true)
          .filter("has_org_keys", "eq", false);

        return count
          ? { data: count }
          : { error: getSupabaseError(error || { message: "Error getting count of pending members" }) };
      },
    }),

    getPendingUsersForOrgKeyExchange: builder.query<Rpc["key_exchange_get_users"]["Returns"], void>({
      providesTags: [{ type: "User", id: "PENDING_USERS" }],
      queryFn: async () => {
        const { data, error } = await supabase.rpc("key_exchange_get_users");

        return data ? { data } : { error: getSupabaseError(error || { message: "Error getting pending members" }) };
      },
    }),

    setOrgSecretsForPendingUsers: builder.mutation<
      void,
      { user_id: string; group_id: string; org_secret_enc: string }[]
    >({
      invalidatesTags: [{ type: "User", id: "PENDING_USERS" }],
      queryFn: async (users) => {
        const { data, error } = await supabase.rpc("key_exchange_set_org_secrets", { users });

        return error
          ? { error: getSupabaseError(error || { message: "Error setting org secrets for pending members." }) }
          : { data };
      },
    }),

    createUser: builder.mutation<
      { url: string; otp: string; expiresAt: string },
      { email?: string; username?: string; groupId: string }
    >({
      invalidatesTags: [{ type: "User", id: "LIST" }],
      queryFn: async ({ email, username, groupId }) => {
        const { data, error } = await supabase.functions.invoke("generate-link", {
          body: JSON.stringify({ email, username, groupId, type: "invite" }),
        });

        return data ? { data } : { error: await getSupabaseFunctionError(error) };
      },
    }),

    inviteUserByEmail: builder.mutation<{ message: string }, { email: string; groupId: string }>({
      invalidatesTags: [{ type: "User", id: "LIST" }],
      queryFn: async ({ email, groupId }) => {
        const { data, error } = await supabase.functions.invoke("invite-user-by-email", {
          body: JSON.stringify({ email, groupId }),
        });
        return data ? { data } : { error: await getSupabaseFunctionError(error) };
      },
    }),

    getOtp: builder.mutation<
      AuthOtpResponse["data"],
      { email: string; url?: string; shouldCreateUser?: boolean; captchaToken?: string }
    >({
      invalidatesTags: [{ type: "User", id: "LIST" }],
      queryFn: async ({ email, url, shouldCreateUser = false, captchaToken }) => {
        const { data, error } = await supabase.auth.signInWithOtp({
          email: email,
          options: {
            shouldCreateUser,
            emailRedirectTo: url,
            captchaToken,
          },
        });
        if (error) {
          return { error: getSupabaseError(error || { message: "Error while generating OTP for a user" }) };
        }
        return { data };
      },
    }),

    resendOtp: builder.mutation<AuthOtpResponse["data"], { email: string }>({
      queryFn: async ({ email }) => {
        const { data, error } = await supabase.auth.resend({
          type: "signup",
          email: email,
        });
        if (error) {
          return { error: getSupabaseError(error || { message: "Error while generating OTP for a user" }) };
        }
        return { data };
      },
    }),

    verifyOtp: builder.mutation<AuthResponse["data"], { email: string; token: string; otpType?: EmailOtpType }>({
      queryFn: async ({ email, token, otpType }) => {
        const { data, error } = await supabase.auth.verifyOtp({
          token,
          email,
          type: otpType || "email",
        });

        if (error) {
          return { error: getSupabaseError(error || { message: "Error while verifying OTP" }) };
        }
        return { data };
      },
    }),

    recoverUser: builder.mutation<{ url: string; otp: string; expiresAt: string }, { email: string }>({
      invalidatesTags: [{ type: "User", id: "LIST" }],
      queryFn: async ({ email }) => {
        const { data, error } = await supabase.functions.invoke("generate-link", {
          body: JSON.stringify({ email, type: "recovery" }),
        });

        return data ? { data } : { error: await getSupabaseFunctionError(error) };
      },
    }),

    deleteUser: builder.mutation<string, { id: string }>({
      invalidatesTags: [
        { type: "User", id: "LIST" },
        { type: "ActionLogs", id: "LIST" },
      ],
      queryFn: async ({ id }) => {
        const { error } = await supabase.functions.invoke("delete-user", {
          body: JSON.stringify({ id }),
        });

        if (error) {
          const message = "Error while deleting user";
          return { error: getSupabaseError(error || { message }) };
        }

        return { data: "ok" };
      },
    }),

    deleteMfa: builder.mutation<boolean, { id: string }>({
      invalidatesTags: [{ type: "User", id: "LIST" }],
      queryFn: async ({ id }) => {
        const { error } = await supabase.rpc("delete_mfa", {
          _user_id: id,
        });

        if (error) {
          const message = "Error while deleting MFA. You may not have permissions.";
          return { error: getSupabaseError(error || { message }) };
        }

        return { data: true };
      },
    }),

    isOwner: builder.query<boolean, void>({
      queryFn: async () => {
        const { data, error } = await supabase.rpc("is_owner");

        return data
          ? { data }
          : {
              error: getSupabaseError(error || { message: "Error checking Owner privilege" }),
            };
      },
    }),

    updateUserGroup: builder.mutation<
      UserGroup[],
      {
        userId: string;
        oldGroupId: string;
        newGroupId: string;
      }
    >({
      invalidatesTags: [{ type: "User", id: "LIST" }, "Group"],
      queryFn: async ({ userId, oldGroupId, newGroupId }) => {
        const { data, error } = await supabase
          .from("users_groups")
          .update({ group_id: newGroupId })
          .eq("user_id", userId)
          .eq("group_id", oldGroupId)
          .select();

        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),

    getDomains: builder.query<Org["domains"], void>({
      providesTags: ["Domain"],
      queryFn: async () => {
        const { data, error } = await supabase.from("orgs").select();

        return data ? { data: data![0].domains } : { error: getSupabaseError(error) };
      },
    }),

    getSlug: builder.query<Org["slug"], void>({
      providesTags: ["Slug"],
      queryFn: async () => {
        const { data, error } = await supabase.from("orgs").select("slug").maybeSingle();

        if (data?.slug) return { data: `@${data.slug}` };

        return {
          error: getSupabaseError(error || { message: "Org Slug not set" }),
        };
      },
    }),

    getGroup: builder.query<GetGroup, { id: string }>({
      providesTags: ["Group"],
      queryFn: async ({ id }) => {
        const { data, error } = await supabase
          .from("groups")
          .select("*, users_groups(count)")
          .order("name", { ascending: true })
          .filter("id", "eq", id)
          .maybeSingle();

        if (data) {
          const users_groups = data.users_groups as unknown as Array<{ count: number }>;
          const permissions = (data.permissions ? data.permissions : {}) as ToolsPermissions;

          const tools_enabled = Object.keys(permissions).flatMap((toolGroupId) => {
            const toolGroup = permissions[toolGroupId as keyof ToolsPermissions];

            if (!toolGroup) return [];

            return Object.keys(toolGroup).flatMap((toolId) => {
              const tool = toolGroup[toolId as keyof typeof toolGroup];

              if (tool && "enabled" in tool && tool["enabled"]) {
                return `${toolGroupId}.${toolId}`;
              }

              return [];
            });
          });

          return {
            data: {
              ...data,
              permissions,
              total_members: users_groups[0].count,
              tools_enabled,
            } as unknown as GetGroup,
          };
        }

        return { error: getSupabaseError(error || { message: "Error while fetching groups" }) };
      },
    }),

    getGroupId: builder.query<{ id: string }, { userId: string }>({
      queryFn: async ({ userId }) => {
        const { data, error } = await supabase.from("users_groups").select("*").eq("user_id", userId).single();

        if (error) {
          const message = "Unknown error updating Group";
          return { error: getSupabaseError(error || { message }) };
        }
        return {
          data: {
            id: data.group_id,
          },
        };
      },
    }),

    getCurrentGroupId: builder.query<{ id: string }, void>({
      queryFn: async () => {
        const groupId = await supabase.rpc("get_group_id");
        if (groupId.error) {
          return { error: getSupabaseError(groupId.error || { message: "Error getting group id" }) };
        }

        return {
          data: {
            id: groupId.data,
          },
        };
      },
    }),

    getGroups: builder.query<GetGroup[], void>({
      providesTags: ["Group"],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("groups")
          .select("*, users_groups(count)")
          .filter("name", "neq", "New Signups")
          .is("deleted_at", null)
          .order("name", { ascending: true });

        if (data) {
          return {
            data: data.map((d) => {
              const users_groups = d.users_groups as unknown as Array<{ count: number }>;
              const permissions = (d.permissions ? d.permissions : {}) as ToolsPermissions;

              const tools_enabled = Object.keys(permissions).flatMap((toolGroupId) => {
                const toolGroup = permissions[toolGroupId as keyof ToolsPermissions];

                if (!toolGroup) return [];

                return Object.keys(toolGroup).flatMap((toolId) => {
                  const tool = toolGroup[toolId as keyof typeof toolGroup];

                  if (tool && "enabled" in tool && tool["enabled"]) {
                    return `${toolGroupId}.${toolId}`;
                  }

                  return [];
                });
              });
              return {
                ...d,
                permissions,
                total_members: users_groups[0].count,
                tools_enabled,
              } as unknown as GetGroup;
            }),
          };
        }

        return { error: getSupabaseError(error) };
      },
    }),

    addGroup: builder.mutation<{ id: string }, Pick<Group, "name" | "description">>({
      invalidatesTags: ["Group"],
      queryFn: async ({ name, description }) => {
        const { data: org_id, error: rpcErr } = await supabase.rpc("get_org_id");

        const { data, error: dbErr } = await supabase
          .from("groups")
          .insert({
            name,
            description,
            org_id,
            permissions: {
              screening: {
                single: { enabled: true },
                batch: { enabled: true },
              },
            },
          })
          .select("id")
          .single();

        if (rpcErr || dbErr) {
          const customErr = rpcErr || dbErr || { message: "Error adding group" };
          return { error: getSupabaseError(customErr) };
        }
        return { data };
      },
    }),

    updateGroup: builder.mutation<null, Group>({
      invalidatesTags: ["Group"],
      queryFn: async ({ id, name, description }) => {
        const { data, error } = await supabase.from("groups").update({ name, description }).eq("id", id);

        if (error) {
          const message = "Unknown error updating Group";
          return { error: getSupabaseError(error || { message }) };
        }
        return { data };
      },
    }),

    updateGroupPermissions: builder.mutation<null, Pick<Group, "id" | "permissions">>({
      invalidatesTags: ["Group"],
      queryFn: async ({ id, permissions }) => {
        const { data, error } = await supabase.from("groups").update({ permissions }).eq("id", id);

        if (error) {
          const message = "Unknown error updating Group";
          return { error: getSupabaseError(error || { message }) };
        }
        return { data };
      },
    }),

    deleteGroup: builder.mutation<null, Pick<Group, "id">>({
      invalidatesTags: ["Group"],
      queryFn: async ({ id }) => {
        const { data, error } = await supabase
          .from("groups")
          .update({ deleted_at: new Date().toISOString() })
          .eq("id", id);

        if (error) {
          const message = "Unknown error deleting Group";
          return { error: getSupabaseError(error || { message }) };
        }
        return { data };
      },
    }),

    getOrg: builder.query<Org, void>({
      providesTags: ["Org"],
      queryFn: async () => {
        const { data, error } = await supabase.from("orgs").select().maybeSingle();

        if (!data) {
          return { error: getSupabaseError({ message: "Couldn't find any orgs" }) };
        }

        if (error) {
          const message = "Unknown error fetching org";
          return { error: getSupabaseError(error || { message }) };
        }
        return { data };
      },
    }),

    updateOrg: builder.mutation<null, Pick<Org, "name">>({
      invalidatesTags: ["Org"],
      queryFn: async ({ name }) => {
        const { data: org_id } = await supabase.rpc("get_org_id");
        if (!org_id) {
          return { error: getSupabaseError({ message: "Error retrieving current Org" }) };
        }

        const { data, error } = await supabase.from("orgs").update({ name }).eq("id", org_id);

        if (error) {
          const message = "Unknown error updating Org";
          return { error: getSupabaseError(error || { message }) };
        }
        return { data };
      },
    }),

    getAssuranceLevel: builder.query<
      AuthMFAGetAuthenticatorAssuranceLevelResponse["data"] & {
        usingOTP: boolean;
        shouldEnrol: boolean;
        hasVerifiedMFA: boolean;
      },
      void
    >({
      providesTags: [{ type: "User", id: "CURRENT" }],
      queryFn: async () => {
        const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

        // A broken session (missing, revoked or unrefreshable) surfaces as an error
        // here. Report it as "no assurance level" rather than an RTK error, otherwise
        // RTK Query keeps serving the last successful value and callers route off a
        // stale, pre-logout assurance level.
        if (!data) {
          if (error) console.warn("getAssuranceLevel: no usable session", error);

          return {
            data: {
              currentLevel: null,
              nextLevel: null,
              currentAuthenticationMethods: [],
              shouldEnrol: true,
              hasVerifiedMFA: false,
              usingOTP: false,
            },
          };
        }

        const { currentLevel: curr, nextLevel: next, currentAuthenticationMethods: authMethods } = data;

        const hasVerifiedMFA = curr === "aal2";
        const shouldEnrol = !(next === "aal2" && next !== curr);
        const usingOTP = authMethods.some((a) => a.method === "otp");

        return {
          data: {
            ...data,
            shouldEnrol,
            hasVerifiedMFA,
            usingOTP,
          },
        };
      },
    }),

    getPasswordResetKeyHolders: builder.query<{ email: string; is_owner: boolean }[], void>({
      queryFn: async () => {
        const { data, error } = await supabase.rpc("get_password_reset_key_holders");

        if (error) {
          return { error: getSupabaseError(error) };
        }

        return { data: data ?? [] };
      },
    }),

    isUsingOTP: builder.query<{ otp: boolean }, void>({
      queryFn: async () => {
        const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

        console.log(data);

        if (data) {
          const isUsingOTP = data?.currentAuthenticationMethods.some((cur) => cur.method === "otp");

          return {
            data: {
              otp: isUsingOTP,
            },
          };
        }
        return { error: getSupabaseError(error) };
      },
    }),

    addPortfolio: builder.mutation<
      Portfolio,
      {
        name: ArrayBuffer;
        encryptedSecret: string;
        groupId?: string;
        isPrivate?: boolean;
        isLargeSite?: boolean;
        metadata?: ArrayBuffer;
        incorporatedHazards?: string[];
        regionType?: "hk" | "global";
      }
    >({
      invalidatesTags: [],
      queryFn: async ({
        name,
        encryptedSecret,
        groupId,
        isPrivate,
        isLargeSite,
        metadata,
        incorporatedHazards,
        regionType,
      }) => {
        const session = await supabase.auth.getSession();
        if (!session.data.session || session.error) {
          return { error: getSupabaseError(session.error || { message: "Session not found" }) };
        }

        const currentGroupId = await supabase.rpc("get_group_id");
        if (currentGroupId.error) {
          return { error: getSupabaseError(currentGroupId.error || { message: "Error getting group id" }) };
        }

        const { data, error } = await supabase
          .from("portfolios")
          .insert({
            name: arrayBufferToHex(name),
            user_id: session.data.session.user.id,
            group_id: groupId || currentGroupId.data,
            secret_enc: encryptedSecret,
            is_private: isPrivate || false,
            portfolio_type: isLargeSite ? "large-site" : "points",
            metadata: metadata && arrayBufferToHex(metadata),
            incorporated_hazards: incorporatedHazards,
            region_type: regionType,
          })
          .select("*")
          .single();

        if (error) {
          const message = "Unknown error adding portfolio";
          return { error: getSupabaseError(error || { message }) };
        }

        return { data };
      },
    }),

    getPortfolio: builder.query<PortfolioView | undefined, string | null | undefined>({
      providesTags: [
        { type: "Portfolio", id: "List" },
        { type: "Portfolio", id: "PortfolioExportUsage" },
      ],
      queryFn: async (id) => {
        if (!id) {
          return {
            data: undefined,
          };
        }

        const { data, error } = await supabase.from("v_portfolios").select().eq("id", id).single();

        if (error) {
          return { error: getSupabaseError(error || { message: "Error while fetching tool logs" }) };
        }

        return { data: data as PortfolioView };
      },
    }),

    getPortfolios: builder.query<
      { items: PortfolioView[] | LargeSiteView[]; count: number },
      {
        isLargeSite: boolean;
        page: number;
        pageSize: number;
        userId?: string;
        isPrivate?: boolean;
        groupId?: string;
        regionTypes?: string[];
      }
    >({
      providesTags: [
        { type: "Portfolio", id: "List" },
        { type: "Portfolio", id: "PortfolioExportUsage" },
      ],
      queryFn: async ({
        isLargeSite,
        page,
        pageSize = ANALYSIS_PAGE_SIZE,
        userId = "",
        isPrivate = false,
        groupId = "",
        regionTypes = [],
      }) => {
        const from = page * pageSize - pageSize;
        const to = page * pageSize - 1;

        const session = await supabase.auth.getSession();
        if (!session.data.session || session.error) {
          return {
            error: getSupabaseError(session.error || { message: "Session not found" }),
          };
        }

        let query = supabase
          .from(isLargeSite ? "v_large_sites" : "v_portfolios")
          .select("*", { count: "exact" })
          .or(`pending.eq.0,user_id.eq.${session.data.session.user.id}`)
          .range(from, to)
          .order("created_at", { ascending: false })
          .limit(pageSize);

        if (userId) {
          query = query.eq("user_id", userId);
        }

        if (isPrivate) {
          query = query.eq("is_private", true);
        }

        if (groupId) {
          query = query.eq("group_id", groupId);
        }

        // v_large_sites has no region_type column to filter on.
        if (!isLargeSite && regionTypes.length > 0) {
          query = query.in("region_type", regionTypes);
        }

        const { data, error, count } = await query;

        if (error) {
          return { error: getSupabaseError(error || { message: "Error while fetching tool logs" }) };
        }

        return {
          data: {
            items: data as PortfolioView[] | LargeSiteView[],
            count: count || 0,
          },
        };
      },
    }),

    /**
     * Which page of the analysis list an analysis falls on, so a re-process
     * confirmed elsewhere can redirect straight to its row. Must be given the
     * same filters the list applies, or the page won't hold the row.
     */
    getPortfolioListPage: builder.query<
      { page: number },
      {
        id: string;
        isLargeSite: boolean;
        pageSize: number;
        userId?: string;
        isPrivate?: boolean;
        groupId?: string;
        regionTypes?: string[];
      }
    >({
      providesTags: [{ type: "Portfolio", id: "List" }],
      queryFn: async ({
        id,
        isLargeSite,
        pageSize = ANALYSIS_PAGE_SIZE,
        userId = "",
        isPrivate = false,
        groupId = "",
        regionTypes = [],
      }) => {
        const session = await supabase.auth.getSession();
        if (!session.data.session || session.error) {
          return {
            error: getSupabaseError(session.error || { message: "Session not found" }),
          };
        }

        const view = isLargeSite ? "v_large_sites" : "v_portfolios";

        const visibleConditions = `pending.eq.0,user_id.eq.${session.data.session.user.id}`;

        // Filtered like the viewable analysis list, so an analysis the list excludes fails here
        // rather than resolving to a page it isn't on.
        let targetQuery = supabase.from(view).select("created_at").or(visibleConditions).eq("id", id);

        if (userId) targetQuery = targetQuery.eq("user_id", userId);
        if (isPrivate) targetQuery = targetQuery.eq("is_private", true);
        if (groupId) targetQuery = targetQuery.eq("group_id", groupId);

        // v_large_sites has no region_type column to filter on.
        if (!isLargeSite && regionTypes.length > 0) targetQuery = targetQuery.in("region_type", regionTypes);

        const { data: target, error: targetError } = await targetQuery.maybeSingle();
        if (targetError) return { error: getSupabaseError(targetError) };

        if (!target?.created_at)
          return { error: getSupabaseError({ message: "Analysis is not in the analysis list" }) };

        // The list is ordered created_at DESC, so an analysis' position in it is
        // the number of rows created after it.
        let query = supabase
          .from(view)
          .select("id", { count: "exact", head: true })
          .or(visibleConditions)
          .gt("created_at", target.created_at);

        if (userId) query = query.eq("user_id", userId);
        if (isPrivate) query = query.eq("is_private", true);
        if (groupId) query = query.eq("group_id", groupId);

        // v_large_sites has no region_type column to filter on.
        if (!isLargeSite && regionTypes.length > 0) query = query.in("region_type", regionTypes);

        const { count, error } = await query;

        if (error) return { error: getSupabaseError(error) };

        return { data: { page: Math.floor((count || 0) / pageSize) + 1 } };
      },
    }),

    getDistinctPortfolioUsers: builder.query<Rpc["get_distinct_portfolio_users"]["Returns"], { isLargeSite: boolean }>({
      queryFn: async ({ isLargeSite }) => {
        const { data, error } = await supabase.rpc("get_distinct_portfolio_users", {
          _portfolio_type: isLargeSite ? "large-site" : "points",
        });

        if (error) {
          return { error: getSupabaseError(error || { message: "Error while fetching tool logs" }) };
        }

        return { data };
      },
    }),

    getToolTemplate: builder.query<string, string>({
      queryFn: async (tool) => {
        const { data, error } = await supabase.rpc("get_live_version_by_tool", {
          _tool: tool,
        });

        if (error) {
          return { error: getSupabaseError(error) };
        }

        return { data };
      },
    }),

    getReportSignedUrl: builder.query<string, { path: string; name: string }>({
      queryFn: async ({ path, name }) => {
        const { data, error } = await supabase.storage
          .from("reports")
          .createSignedUrl(path, 60 * 5, { download: name });

        if (error) {
          return { error: getSupabaseError(error) };
        }

        return { data: data.signedUrl };
      },
      keepUnusedDataFor: 0,
    }),

    getExistingCollectionReport: builder.query({
      queryFn: async (id) => {
        const { data, error } = await supabase
          .rpc("get_existing_report", {
            _collection: id,
          })
          .maybeSingle();

        if (error) {
          return { error: getSupabaseError(error) };
        }

        return { data };
      },
    }),

    updatePortfolioReport: builder.mutation<
      Portfolio,
      {
        id: string;
        summaryId: string | null;
        isGenerated?: boolean;
      }
    >({
      invalidatesTags: [
        { type: "Portfolio", id: "List" },
        { type: "Portfolio", id: "ActiveSummaryList" },
      ],
      queryFn: async ({ id, summaryId, isGenerated = false }) => {
        const { data, error } = await supabase
          .from("portfolios")
          .update({
            summary_id: summaryId,
            is_summary_generated: isGenerated,
          })
          .eq("id", id)
          .select()
          .single();

        if (error) {
          const message = "Unknown error updating Portfolio";
          return { error: getSupabaseError(error || { message }) };
        }

        return { data };
      },
    }),

    updatePortfolioAdaptationReport: builder.mutation<
      SupabaseDatabase["public"]["Tables"]["adaptations"]["Row"],
      { id: string; summaryId: string | null; isGenerated?: boolean }
    >({
      invalidatesTags: ["Adaptations"],
      queryFn: async ({ id, summaryId, isGenerated = false }) => {
        const { data, error } = await supabase
          .from("adaptations")
          .update({ summary_id: summaryId, is_summary_generated: isGenerated })
          .eq("id", id)
          .select()
          .single();
        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),

    getActiveAdaptationSummaries: builder.query<
      (SupabaseDatabase["public"]["Tables"]["adaptations"]["Row"] & {
        portfolios: Pick<
          SupabaseDatabase["public"]["Tables"]["portfolios"]["Row"],
          "name" | "group_id" | "secret_enc" | "is_private" | "user_id" | "portfolio_type"
        > | null;
      })[],
      void
    >({
      providesTags: [{ type: "Adaptations", id: "ActiveSummaryList" }],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("adaptations")
          .select(
            "*, portfolios!adaptations_portfolio_id_fkey(name, group_id, secret_enc, is_private, user_id, portfolio_type)"
          )
          .eq("is_summary_generated", false)
          .not("summary_id", "is", null)
          .is("deleted_at", null);
        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),

    getActiveSummaries: builder.query<{ items: PortfolioView[] | LargeSiteView[]; count: number }, void>({
      providesTags: [{ type: "Portfolio", id: "ActiveSummaryList" }],
      queryFn: async () => {
        const [portfolios, largeSites] = await Promise.all([
          supabase
            .from("v_portfolios")
            .select()
            .filter("is_summary_generated", "eq", false)
            .not("summary_id", "is", null),
          supabase
            .from("v_large_sites")
            .select()
            .filter("is_summary_generated", "eq", false)
            .not("summary_id", "is", null),
        ]);

        if (portfolios.error) {
          return {
            error: getSupabaseError(portfolios.error || { message: "Error while fetching active portfolio summaries" }),
          };
        }

        if (largeSites.error) {
          return {
            error: getSupabaseError(
              largeSites.error || { message: "Error while fetching active large site summaries" }
            ),
          };
        }

        return {
          data: {
            items: [...portfolios.data, ...largeSites.data] as PortfolioView[] | LargeSiteView[],
            count: (portfolios.count || 0) + (largeSites.count || 0),
          },
        };
      },
    }),

    updatePortfolioDetails: builder.mutation<
      Portfolio,
      {
        id: string;
        groupId: string;
        name: ArrayBuffer;
        isPrivate: boolean;
        secretEnc: string;
      }
    >({
      invalidatesTags: [{ type: "Portfolio", id: "List" }],
      queryFn: async ({ id, groupId, name, isPrivate, secretEnc }) => {
        const { data, error } = await supabase
          .from("portfolios")
          .update({
            group_id: groupId,
            name: arrayBufferToHex(name),
            is_private: isPrivate,
            secret_enc: secretEnc,
          })
          .eq("id", id)
          .select()
          .single();

        if (error) {
          const message = "Unknown error updating Portfolio";
          return { error: getSupabaseError(error || { message }) };
        }

        return { data };
      },
    }),

    updatePortfolioRerun: builder.mutation<null, { id: string }>({
      invalidatesTags: [{ type: "Portfolio", id: "List" }],
      queryFn: async ({ id }) => {
        const session = await supabase.auth.getSession();
        if (!session.data.session || session.error) {
          return { error: getSupabaseError(session.error || { message: "Session not found" }) };
        }

        const { error } = await supabase
          .from("portfolios")
          .update({
            rerun_user_id: session.data.session.user.id,
            rerun_at: new Date().toISOString(),
          })
          .eq("id", id);

        if (error) {
          return { error: getSupabaseError(error) };
        }

        return { data: null };
      },
    }),

    updatePortfolioStamps: builder.mutation<
      Rpc["set_portfolio_stamps"]["Returns"],
      {
        id: string;
        stamps: string[];
      }
    >({
      queryFn: async ({ id, stamps }) => {
        const { data, error } = await supabase.rpc("set_portfolio_stamps", {
          _stamps: stamps,
          _id: id,
        });

        if (error) {
          const message = "Unknown error updating Portfolio Stamps";
          return { error: getSupabaseError(error || { message }) };
        }

        return { data };
      },
    }),

    updateAdaptationsStamps: builder.mutation<
      Rpc["set_adaptations_stamps"]["Returns"],
      {
        ids: string[];
        stamps: string[];
      }
    >({
      queryFn: async ({ ids, stamps }) => {
        const { data, error } = await supabase.rpc("set_adaptations_stamps", {
          _stamps: stamps,
          _ids: ids,
        });

        if (error) {
          const message = "Unknown error updating Resilience Pathway Action Stamps";
          return { error: getSupabaseError(error || { message }) };
        }

        return { data };
      },
    }),

    updateAdaptationProgressCounts: builder.mutation<
      null,
      {
        id: string;
        total: number;
        success: number;
        error: number;
        pending: number;
      }
    >({
      invalidatesTags: [{ type: "Adaptations", id: "List" }],
      queryFn: async ({ id, total, pending, error, success }) => {
        const { error: adaptationError } = await supabase
          .from("adaptations")
          .update({ total, pending, error, success })
          .eq("id", id);
        if (adaptationError) {
          return { error: getSupabaseError(adaptationError) };
        }
        return { data: null };
      },
    }),

    updatePortfolioProgressCounts: builder.mutation<
      Portfolio,
      {
        total: number;
        success: number;
        error: number;
        pending: number;
        id: string;
      }
    >({
      invalidatesTags: [{ type: "Portfolio", id: "List" }],
      queryFn: async (portfolio) => {
        const { data, error } = await supabase
          .from("portfolios")
          .update({
            total: portfolio.total,
            pending: portfolio.pending,
            error: portfolio.error,
            success: portfolio.success,
          })
          .eq("id", portfolio.id)
          .single();
        if (error) {
          const message = "Unknown error updating Portfolio";
          return { error: getSupabaseError(error || { message }) };
        }

        return { data };
      },
    }),

    getAllPortfolios: builder.query<
      Rpc["get_org_portfolios"]["Returns"],
      {
        dateFrom: string;
        dateUntil: string;
        pageSize: number;
        page: number;
        regionType?: string;
        userId?: string;
        groupId?: string;
      }
    >({
      providesTags: [{ type: "Portfolio", id: "ListIncludingDeleted" }],
      queryFn: async ({ dateFrom, dateUntil, pageSize = 10, page = 1, regionType, userId, groupId }) => {
        const offset = page * pageSize - pageSize;

        const { data, error } = await supabase.rpc("get_org_portfolios", {
          _date_from: dateFrom,
          _date_until: dateUntil,
          _limit: pageSize,
          _offset: offset,
          _region_type: regionType ?? null,
          _user_id: userId ?? null,
          _group_id: groupId ?? null,
        } as any);

        if (error) {
          const message = "Unknown error retrieving Portfolio list";
          return { error: getSupabaseError(error || { message }) };
        }

        return { data };
      },
    }),

    getAllLargeSites: builder.query<
      Rpc["get_org_large_sites"]["Returns"],
      { dateFrom: string; dateUntil: string; pageSize: number; page: number; userId?: string; groupId?: string }
    >({
      providesTags: [{ type: "Portfolio", id: "ListIncludingDeleted" }],
      queryFn: async ({ dateFrom, dateUntil, pageSize = 10, page = 1, userId, groupId }) => {
        const offset = page * pageSize - pageSize;

        const { data, error } = await supabase.rpc("get_org_large_sites", {
          _date_from: dateFrom,
          _date_until: dateUntil,
          _limit: pageSize,
          _offset: offset,
          _user_id: userId ?? null,
          _group_id: groupId ?? null,
        } as any);

        if (error) {
          const message = "Unknown error retrieving Large Site list";
          return { error: getSupabaseError(error || { message }) };
        }

        return { data };
      },
    }),

    addPortfolioAssets: builder.mutation<
      { id: string; data: string | null; encrypted_id: string }[],
      { encrypted_id: ArrayBuffer; portfolio_id: string; data: ArrayBuffer }[]
    >({
      invalidatesTags: [],
      queryFn: async (assets) => {
        const payload = assets.map((asset) => ({
          ...asset,
          encrypted_id: arrayBufferToHex(asset.encrypted_id),
          data: arrayBufferToHex(asset.data),
        }));

        const { data, error } = await supabase.from("portfolio_assets").insert(payload).select("id,data,encrypted_id");

        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),

    /**
     * Retrieves assets with errors for a Portfolio Export portfolio within a given range.
     */
    getErroneousPortfolioAssets: builder.query<
      PortfolioAsset[],
      {
        portfolioId: string;
        start: number;
        length: number;
      }
    >({
      providesTags: [{ type: "PortfolioAssets", id: "List" }],
      queryFn: async ({ portfolioId, start, length }) => {
        const { data, error } = await supabase
          .from("portfolio_assets")
          .select()
          .filter("portfolio_id", "eq", portfolioId)
          .filter("errors", "neq", null)
          .range(start, start + length - 1)
          .order("created_at");

        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),

    updatePortfolioAssetsWithErrors: builder.mutation<{ id: string }[], { errors: ArrayBuffer; id: string }>({
      invalidatesTags: [],
      queryFn: async (asset) => {
        const { data, error } = await supabase
          .from("portfolio_assets")
          .update({ errors: arrayBufferToHex(asset.errors) })
          .eq("id", asset.id)
          .select("id");

        return data
          ? { data }
          : { error: getSupabaseError(error || { message: "Something went wrong while updating portfolio assets" }) };
      },
    }),

    setPortfolioAssetIsProcessedToTrue: builder.mutation<string, { id: string }[]>({
      invalidatesTags: [],
      queryFn: async (assets) => {
        const { data, error } = await supabase.rpc("set_portfolio_assets_as_processed", {
          payload: assets,
        });

        return data ? { data } : { error: getSupabaseError(error || { message: "asdf" }) };
      },
    }),

    setAdaptationPathwayActionIsProcessedToTrue: builder.mutation<string, { id: string }[]>({
      invalidatesTags: [],
      queryFn: async (actions) => {
        const { data, error } = await supabase.rpc("set_adaptation_pathway_actions_as_processed", {
          payload: actions,
        });

        return data ? { data } : { error: getSupabaseError(error || { message: "asdf" }) };
      },
    }),

    /**
     * Retrieves successful assets for a Portfolio Export portfolio within a given range.
     *
     * Assets are bulk-inserted and `created_at` is the same per chunk.
     * Also order it by `id` to make fetches deterministic
     */
    getSuccessfulPortfolioAssets: builder.query<
      PortfolioAsset[],
      {
        portfolioId: string;
        start: number;
        length: number;
      }
    >({
      providesTags: [{ type: "PortfolioAssets", id: "List" }],
      queryFn: async ({ portfolioId, start, length }) => {
        const { data, error } = await supabase
          .from("portfolio_assets")
          .select()
          .filter("portfolio_id", "eq", portfolioId)
          .is("errors", null)
          .range(start, start + length - 1)
          .order("created_at")
          .order("id");

        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),

    /**
     * Retrieves a given number of unprocessed portfolio assets (default: 100).
     */
    getUnprocessedPortfolioAssetIds: builder.query<
      EncryptedAnalysisWithPointId[],
      {
        portfolioId: string;
        length?: number;
      }
    >({
      queryFn: async ({ portfolioId, length = 100 }) => {
        const { data, error } = await supabase
          .from("portfolio_assets")
          .select("supabaseId:id,id:encrypted_id,data")
          .filter("portfolio_id", "eq", portfolioId)
          .or("is_processed.is.false,is_processed.is.null")
          .range(0, length - 1)
          .order("created_at");

        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),

    getUnprocessedPortfolioAssetCount: builder.query<number | null, { portfolioId: string }>({
      queryFn: async ({ portfolioId }) => {
        const { count, error } = await supabase
          .from("portfolio_assets")
          .select("*", { count: "exact", head: true })
          .filter("portfolio_id", "eq", portfolioId)
          .or("is_processed.is.false,is_processed.is.null");

        if (error) {
          return {
            error: getSupabaseError(error || { message: "Error while getting unprocessed portfolio asset count." }),
          };
        }

        return {
          data: count,
        };
      },
    }),

    /**
     * Returns number of assets with errors within a given portfolio.
     */
    getPortfolioAssetErrorsCount: builder.query<number, string>({
      queryFn: async (portfolioId: string) => {
        const { count, error } = await supabase
          .from("portfolio_assets")
          .select("*", { count: "exact", head: true })
          .filter("portfolio_id", "eq", portfolioId)
          .neq("errors", null);

        return !error ? { data: count || 0 } : { error: getSupabaseError(error) };
      },
    }),

    /**
     * Returns processed number of assets within a given portfolio.
     */
    getPortfolioAssetProcessedCount: builder.query<number, string>({
      queryFn: async (portfolioId: string) => {
        const { count, error } = await supabase
          .from("portfolio_assets")
          .select("*", { count: "exact", head: true })
          .filter("portfolio_id", "eq", portfolioId)
          .is("is_processed", true);

        return !error ? { data: count || 0 } : { error: getSupabaseError(error) };
      },
    }),

    /**
     * Returns total number of assets within a given portfolio.
     */
    getPortfolioAssetTotalCount: builder.query<number, string>({
      queryFn: async (portfolioId: string) => {
        const { count, error } = await supabase
          .from("portfolio_assets")
          .select("*", { count: "exact", head: true })
          .filter("portfolio_id", "eq", portfolioId);

        return !error ? { data: count || 0 } : { error: getSupabaseError(error) };
      },
    }),

    /**
     * Returns total, processed and error count of assets within a given portfolio.
     */
    getPortfolioAssetCount: builder.query<
      {
        total: number;
        processed: number;
        errors: number;
      },
      string
    >({
      queryFn: async (portfolio_id: string) => {
        const { data, error } = await supabase
          .rpc("get_portfolio_asset_count", {
            _portfolio_id: portfolio_id,
          })
          .maybeSingle();

        return !error
          ? { data: data as { total: number; processed: number; errors: number } }
          : { error: getSupabaseError(error) };
      },
    }),

    /**
     * Retrieves all asset IDs for a given portfolio.
     */
    getAllPortfolioAssetIds: builder.query<Pick<PortfolioAsset, "id" | "encrypted_id">[], string>({
      queryFn: async (portfolioId: string) => {
        const { data, error } = await supabase
          .from("portfolio_assets")
          .select()
          .filter("portfolio_id", "eq", portfolioId)
          .select("id,encrypted_id");

        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),

    getAllPortfolioAdaptationAnalysisIds: builder.query<
      Pick<AdaptationPathwayAction, "id" | "encrypted_id" | "data" | "portfolio_asset_id">[],
      { portfolioId: string; adaptationId?: string }
    >({
      queryFn: async ({ portfolioId, adaptationId }) => {
        let query = supabase.from("adaptation_pathway_actions").select("id,encrypted_id,data,portfolio_asset_id");

        if (adaptationId) {
          query = query.eq("adaptation_id", adaptationId);
        } else if (portfolioId) {
          query = query.eq("portfolio_id", portfolioId);
        }

        const { data, error } = await query;
        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),

    getAllPortfolioAdaptationPathwayActions: builder.query<
      GetAdaptationActionResponse[],
      {
        portfolioId: string;
        adaptationId?: string;
        previousBatchEndId?: string;
        limit: number;
      }
    >({
      queryFn: async ({ portfolioId, adaptationId, previousBatchEndId: afterId, limit }) => {
        // order by id bc its indexed and unique
        let query = supabase
          .from("adaptation_pathway_actions")
          .select("*")
          .is("errors", null)
          .is("deleted_at", null)
          .order("id", { ascending: true })
          .limit(limit);

        if (adaptationId) {
          query = query.eq("adaptation_id", adaptationId);
        } else if (portfolioId) {
          query = query.eq("portfolio_id", portfolioId);
        }

        if (afterId) {
          query = query.gt("id", afterId);
        }

        const { data, error } = await query;
        return data ? { data: data as GetAdaptationActionResponse[] } : { error: getSupabaseError(error) };
      },
    }),

    /**
     * Retrieves all successful assets for a given portfolio.
     */
    getAllPortfolioAssetsEncrypted: builder.query<
      {
        encryptedId: string;
        data: string | null;
        id: string;
      }[],
      string
    >({
      queryFn: async (portfolioId: string) => {
        const { data, error } = await supabase
          .from("portfolio_assets")
          .select()
          .filter("portfolio_id", "eq", portfolioId)
          .is("errors", null)
          .select("data,encrypted_id,id");

        return data
          ? {
              data:
                data?.map(({ data, encrypted_id: encryptedId, id }) => ({
                  data,
                  encryptedId,
                  id,
                })) || [],
            }
          : { error: getSupabaseError(error) };
      },
    }),

    getAdaptations: builder.query<
      SupabaseDatabase["public"]["Tables"]["adaptations"]["Row"][],
      { portfolioId: string; adaptationTypes?: AdaptationType[] }
    >({
      providesTags: ["Adaptations"],
      queryFn: async ({ portfolioId, adaptationTypes = [] }) => {
        let query = supabase.from("adaptations").select("*").filter("portfolio_id", "eq", portfolioId);
        if (adaptationTypes.length) {
          query = query.in("adaptation_type", adaptationTypes);
        }
        const { data, error } = await query;
        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),
    updatePortfolioAutoAdaptationStamps: builder.mutation<null, { portfolioId: string; stamps: string[] }>({
      invalidatesTags: ["Adaptations"],
      queryFn: async ({ portfolioId, stamps }) => {
        const { error } = await supabase.from("adaptations").update({ stamps }).eq("portfolio_id", portfolioId);
        return error ? { error: getSupabaseError(error) } : { data: null };
      },
    }),
    getAdaptationPathwaysWithActions: builder.query<GetPointAdaptationResponse[], any>({
      providesTags: ["AdaptationPathways", "AdaptationPathwayActions"],
      queryFn: async ({
        portfolioId,
        adaptationTypes,
        portfolioAssetId,
      }: {
        portfolioId: string;
        adaptationTypes: AdaptationType[];
        portfolioAssetId?: string;
      }) => {
        let query = supabase.from("adaptation_pathways").select("*,adaptation_pathway_actions(*)");
        if (portfolioAssetId) {
          query = query.filter("portfolio_asset_id", "eq", portfolioAssetId);
        } else if (portfolioId) {
          query = query.filter("portfolio_id", "eq", portfolioId);
        }
        if (adaptationTypes.length) query = query.in("adaptation_type", adaptationTypes);

        const { data, error } = await query;

        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),

    getUnprocessedAdaptationPathwayActions: builder.query<
      EncryptedAnalysisWithPointId[],
      {
        portfolioId: string;
        portfolioAssetIds: string[] | undefined;
      }
    >({
      queryFn: async ({ portfolioId, portfolioAssetIds = [] }) => {
        if (!portfolioId && !portfolioAssetIds?.length) {
          return { data: [] };
        }

        let query = supabase
          .from("adaptation_pathway_actions")
          .select("supabaseId:id,id:encrypted_id,data")
          .or("is_processed.is.false,is_processed.is.null")
          .order("created_at");

        if (portfolioId) query = query.eq("portfolio_id", portfolioId);
        if (portfolioAssetIds?.length) query = query.in("portfolio_asset_id", portfolioAssetIds);

        const { data, error } = await query;
        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),
    addAdaptations: builder.mutation<
      SupabaseDatabase["public"]["Tables"]["adaptations"]["Row"][],
      { portfolio_id: string; adaptation_type: AdaptationType }[]
    >({
      invalidatesTags: ["Adaptations"],
      queryFn: async (adaptations) => {
        const session = await supabase.auth.getSession();

        const userId = session.data.session?.user?.id;

        if (!session.data.session || session.error || !userId) {
          return { error: getSupabaseError(session.error || { message: "Session not found" }) };
        }

        const { data, error } = await supabase
          .from("adaptations")
          .insert(adaptations.map((adaptation) => ({ ...adaptation, user_id: userId })))
          .select("*");
        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),
    addAdaptationPathways: builder.mutation<
      SupabaseDatabase["public"]["Tables"]["adaptation_pathways"]["Row"][],
      {
        portfolio_id: string;
        portfolio_asset_id: string;
        adaptation_id: string;
        adaptation_type: AdaptationType;
      }[]
    >({
      invalidatesTags: ["AdaptationPathways"],
      queryFn: async (payload) => {
        const { data, error } = await supabase.from("adaptation_pathways").insert(payload).select("*");
        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),
    addAdaptationPathwayActions: builder.mutation<
      SupabaseDatabase["public"]["Tables"]["adaptation_pathway_actions"]["Insert"][],
      {
        encrypted_id: ArrayBuffer;
        data: ArrayBuffer;
        adaptation_pathway_id: string;
        portfolio_asset_id: string;
        portfolio_id: string;
        adaptation_id: string;
        adaptation_type: AdaptationType;
        is_processed?: boolean;
      }[]
    >({
      invalidatesTags: ["AdaptationPathways", "AdaptationPathwayActions"],
      queryFn: async (pathwayActions) => {
        const payload = pathwayActions.map(
          ({
            encrypted_id,
            data,
            portfolio_id,
            portfolio_asset_id,
            adaptation_id,
            adaptation_pathway_id,
            adaptation_type,
            is_processed,
          }) => ({
            encrypted_id: arrayBufferToHex(encrypted_id),
            data: arrayBufferToHex(data),
            portfolio_id,
            portfolio_asset_id,
            adaptation_id,
            adaptation_pathway_id,
            adaptation_type,
            is_processed,
          })
        );

        const { data: actionData, error } = await supabase
          .from("adaptation_pathway_actions")
          .insert(payload)
          .select("*");
        return actionData ? { data: actionData } : { error: getSupabaseError(error) };
      },
    }),

    setPortfolioAssetsProcessed: builder.mutation<null, string[]>({
      invalidatesTags: ["PortfolioAssets"],
      queryFn: async (ids) => {
        const { data, error } = await supabase
          .from("portfolio_assets")
          .update({
            is_processed: true,
          })
          .in("id", ids);

        if (error) {
          const message = "Unknown error updating Portfolio Assets";
          return { error: getSupabaseError(error || { message }) };
        }
        return { data };
      },
    }),

    /**
     * Sets errors on Portfolio Assets.
     */
    setPortfolioAssetsErrors: builder.mutation<
      string,
      {
        portfolioId: string;
        rows: {
          id: string;
          encryptedId: ArrayBuffer;
          errors: ArrayBuffer;
        }[];
      }
    >({
      invalidatesTags: ["PortfolioAssets"],
      queryFn: async ({ portfolioId, rows }) => {
        const data = rows.map(({ id, encryptedId, errors }) => ({
          id,
          portfolio_id: portfolioId,
          encrypted_id: arrayBufferToHex(encryptedId),
          errors: arrayBufferToHex(errors),
        }));

        const { error } = await supabase.from("portfolio_assets").upsert(data);

        if (error) {
          const message = "Unknown error updating Portfolio Assets";
          return { error: getSupabaseError(error || { message }) };
        }

        return {
          data: "success",
        };
      },
    }),

    /**
     * Deletes a Portfolio Export portfolio.
     *
     * Note: Portfolio list is manually filtered on delete. Adding invalidateTags for Portfolio will cause flickering.
     */
    deletePortfolio: builder.mutation<null, string>({
      invalidatesTags: [
        { type: "Portfolio", id: "ListIncludingDeleted" },
        { type: "Portfolio", id: "List" },
        { type: "PortfolioAssets", id: "List" },
        "Adaptations",
        "AdaptationPathways",
        "AdaptationPathwayActions",
      ],
      queryFn: async (portfolioId) => {
        const actions = await supabase.from("adaptation_pathway_actions").delete().eq("portfolio_id", portfolioId);
        if (actions.error) return { error: getSupabaseError(actions.error) };

        const pathways = await supabase.from("adaptation_pathways").delete().eq("portfolio_id", portfolioId);
        if (pathways.error) return { error: getSupabaseError(pathways.error) };

        const adaptations = await supabase.from("adaptations").delete().eq("portfolio_id", portfolioId);
        if (adaptations.error) return { error: getSupabaseError(adaptations.error) };

        const portfolioAssets = await supabase.from("portfolio_assets").delete().eq("portfolio_id", portfolioId);
        if (portfolioAssets.error) return { error: getSupabaseError(portfolioAssets.error) };

        // portfolio soft delete
        const portfolio = await supabase
          .from("portfolios")
          .update({ deleted_at: new Date().toISOString() })
          .eq("id", portfolioId);

        if (portfolio.error) {
          const message = "Unknown error deleting Portfolio";
          return { error: getSupabaseError(portfolio.error || { message }) };
        }
        return { data: null };
      },
    }),

    deletePortfolioAdaptations: builder.mutation<null, string>({
      invalidatesTags: [
        { type: "Portfolio", id: "List" },
        "Adaptations",
        "AdaptationPathways",
        "AdaptationPathwayActions",
      ],
      queryFn: async (portfolioId) => {
        const actions = await supabase.from("adaptation_pathway_actions").delete().eq("portfolio_id", portfolioId);
        if (actions.error) return { error: getSupabaseError(actions.error) };

        const pathways = await supabase.from("adaptation_pathways").delete().eq("portfolio_id", portfolioId);
        if (pathways.error) return { error: getSupabaseError(pathways.error) };

        const adaptations = await supabase.from("adaptations").delete().eq("portfolio_id", portfolioId);
        if (adaptations.error) return { error: getSupabaseError(adaptations.error) };

        return { data: null };
      },
    }),

    generateUsername: builder.query<string, void>({
      queryFn: async () => {
        const { data, error } = await supabase.functions.invoke("generate-username");

        return data ? { data: data.username } : { error: getSupabaseError(error) };
      },
    }),

    showMaxPortfolioModal: builder.query<boolean, void>({
      providesTags: [{ type: "Org", id: "portfolioSize" }],
      queryFn: async () => {
        const isOwner = await supabase.rpc("is_owner");
        if (isOwner.error) {
          return { error: getSupabaseError(isOwner.error || { message: "Error checking Owner privilege" }) };
        }
        if (!isOwner.data) {
          return { data: false };
        }

        const { data, error } = await supabase.from("orgs").select("portfolio_size").maybeSingle();

        if (!data) {
          return { error: getSupabaseError({ message: "Couldn't find any rows" }) };
        }

        if (error) {
          return { error: getSupabaseError(error || { message: "Error fetching max portfolio size" }) };
        }

        return { data: data.portfolio_size === null };
      },
    }),

    setMaxPortfolioSize: builder.mutation<null, { size: number }>({
      invalidatesTags: [
        { type: "Org", id: "portfolioSize" },
        { type: "Org", id: "ToolUsage" },
      ],
      queryFn: async ({ size }) => {
        const { data: org_id, error: rpcErr } = await supabase.rpc("get_org_id");

        const { data, error } = await supabase.from("orgs").update({ portfolio_size: size }).filter("id", "eq", org_id);

        if (rpcErr || error) {
          const customErr = rpcErr || error || { message: "Error setting portfolio size" };
          return { error: getSupabaseError(customErr) };
        }
        return { data };
      },
    }),

    getQuotaAllocations: builder.query<ToolQuota[], void>({
      queryFn: async () => {
        const { data, error } = await supabase
          .from("tool_quotas")
          .select()
          .in("tool", ["portfolio-insights", "large-site", "screening"])
          .order("created_at", { ascending: false })
          .order("tool");

        if (error) {
          return { error: getSupabaseError(error || { message: "Error fetching max portfolio size" }) };
        }

        return { data };
      },
    }),

    logToolUsage: builder.mutation<
      null,
      { user_id: string; group_id: string; tool_id: string; count: number; archetype: string }
    >({
      invalidatesTags: [],
      queryFn: async ({ user_id, group_id, tool_id, count, archetype }) => {
        const { error } = await supabase.from("tool_logs").insert({
          user_id,
          group_id,
          tool_id,
          count,
          archetype,
        });

        if (error) {
          return { error: getSupabaseError(error || { message: "Error while logging tool usage" }) };
        }

        return { data: null };
      },
    }),

    getToolLogs: builder.query<
      {
        data: SupabaseDatabase["public"]["Views"]["v_tool_logs"]["Row"][];
        count: number;
      },
      {
        toolIds: string[];
        page: number;
        pageSize: number;
        dateFrom: string;
        dateUntil: string;
        sortBy?: string;
        sortAsc?: boolean;
        userId?: string;
        groupId?: string;
      }
    >({
      queryFn: async ({
        toolIds,
        page,
        pageSize,
        dateFrom,
        dateUntil,
        sortBy = "created_at",
        sortAsc = false,
        userId,
        groupId,
      }) => {
        const from = page * pageSize - pageSize;
        const to = page * pageSize - 1;

        let query = supabase
          .from("v_tool_logs")
          .select("*", { count: "exact" })
          .in("tool_id", toolIds)
          .range(from, to)
          .filter("created_at", "gte", dateFrom)
          .filter("created_at", "lt", dateUntil)
          .filter("count", "gt", 0);

        if (userId) query = query.eq("user_id", userId);
        if (groupId) query = query.eq("group_id", groupId);

        query = query.order(sortBy, { ascending: sortAsc });

        const { data, error, count } = await query;

        if (error) {
          return { error: getSupabaseError(error || { message: "Error while fetching tool logs" }) };
        }

        return {
          data: {
            data,
            count: count || 0,
          },
        };
      },
    }),

    getToolLogsCount: builder.query<number, { toolId: string; dateFrom: string; dateUntil: string }>({
      queryFn: async ({ toolId, dateFrom, dateUntil }) => {
        const { data, error } = await supabase.rpc("get_tool_logs_count", {
          _tool_id: toolId,
          _date_from: dateFrom,
          _date_until: dateUntil,
        });

        if (error) {
          return { error: getSupabaseError(error || { message: "Error fetching max portfolio size" }) };
        }

        return { data: data || 0 };
      },
    }),

    getToolUsage: builder.query<
      GetToolUsageResponse,
      { dateFrom?: string; dateUntil?: string; ignoreDateFrom?: boolean; activeQuotaOnly?: boolean }
    >({
      providesTags: [{ type: "Org", id: "ToolUsage" }],
      queryFn: async ({
        dateFrom = getUTCDate(new Date().getFullYear(), 0, 1),
        dateUntil = getUTCDate(new Date().getFullYear() + 1, 0, 1),
        ignoreDateFrom = false,
        activeQuotaOnly = false,
      }) => {
        const _dateFrom = ignoreDateFrom ? getUTCDate(new Date("2022-01-01").getFullYear(), 0, 1) : dateFrom;

        const { data: response, error } = await supabase.rpc("get_tool_usage", {
          _date_from: _dateFrom,
          _date_until: dateUntil,
          _active_quota_only: activeQuotaOnly,
        });

        const data = response as GetToolUsageRpcResponse;

        if (error) {
          return { error: getSupabaseError(error || { message: "Error fetching tool usage" }) };
        }

        const getCurrentToolUsage = ({
          usage,
          quota,
          buffer,
          isLargeSite = false,
        }: {
          usage: number;
          quota: number;
          buffer: ToolLimit;
          isLargeSite?: boolean;
        }): CurrentToolUsage => {
          const usagePercent = quota > 0 ? (1 - (quota - usage) / quota) * 100 : 100;
          const isInfiniteQuota = buffer.hard_limit_percent === 0;
          const isEnabled = isInfiniteQuota ? true : quota === 0 ? false : buffer.hard_limit_percent > usagePercent;

          const getWarningLevel = (): "error" | "warn" | "none" => {
            if (isInfiniteQuota) return "none";
            if (usagePercent >= 100) return "error";
            if (isLargeSite) {
              if (quota - usage == 1) return "warn";
            } else {
              if (usagePercent >= 80) return "warn";
            }
            return "none";
          };

          return {
            alwaysEnabled: isInfiniteQuota,
            enabled: isEnabled,
            showWarning: getWarningLevel(),
            quota,
            usage,
            usagePercent,
            hardLimitPercent: buffer.hard_limit_percent,
          };
        };

        const currentPortfolioExportUsage = getCurrentToolUsage({
          usage: data.usage.portfolioInsights,
          quota: data.quotas.portfolioInsights.quota,
          buffer: data.buffers.portfolioInsights,
        });
        const currentPortfolioInsightsGlobalUsage = getCurrentToolUsage({
          usage: data.usage.portfolioInsightsGlobal ?? 0,
          quota: data.quotas.portfolioInsightsGlobal?.quota ?? 0,
          buffer: data.buffers.portfolioInsightsGlobal ?? { absolute: 0, percent: 0, hard_limit_percent: 100 },
        });
        const currentLargeSiteUsage = getCurrentToolUsage({
          usage: data.usage.largeSite,
          quota: data.quotas.largeSite.quota,
          buffer: data.buffers.largeSite,
        });
        const currentScreeningUsage = getCurrentToolUsage({
          usage: data.usage.screening,
          quota: data.quotas.screening.quota,
          buffer: data.buffers.screening,
        });
        const currentSingleQuotaUsage = data.singleQuotaMode
          ? getCurrentToolUsage({
              usage: data.usage.screening + data.usage.portfolioInsights,
              quota: data.quotas.portfolioInsights.quota,
              buffer: data.buffers.portfolioInsights,
            })
          : null;

        return {
          data: {
            ...data,
            portfolioInsights: currentPortfolioExportUsage,
            portfolioInsightsGlobal: currentPortfolioInsightsGlobalUsage,
            largeSite: currentLargeSiteUsage,
            screening: currentScreeningUsage,
            singleQuotaUsage: currentSingleQuotaUsage,
          },
        };
      },
    }),

    getToolUsageByMonth: builder.query<
      SupabaseDatabase["public"]["Views"]["v_tool_usage"]["Row"][],
      { dateFrom: string; dateUntil: string; toolId?: string; regionType?: string }
    >({
      queryFn: async ({ dateFrom, dateUntil, toolId = "*", regionType }) => {
        let query = supabase
          .from("v_tool_usage")
          .select("*")
          .filter("date", "gte", dateFrom)
          .filter("date", "lt", dateUntil)
          .filter("tool_id", "like", toolId);

        if (regionType) {
          query = query.filter("region_type", "eq", regionType);
        }

        const { data, error } = await query.order("date", { ascending: true });

        if (error) {
          return { error: getSupabaseError(error || { message: "Error while fetching tool logs" }) };
        }

        return { data };
      },
    }),

    getSupportEmail: builder.query<Rpc["get_support_email"]["Returns"], void>({
      queryFn: async () => {
        const { data, error } = await supabase.rpc("get_support_email");

        if (error) {
          return { error: getSupabaseError(error || { message: "Error while fetching support email." }) };
        }

        return { data };
      },
    }),

    getCurrentCollectionVersion: builder.query<Rpc["get_current_collection_version"]["Returns"], void>({
      queryFn: async () => {
        const { data, error } = await supabase.rpc("get_current_collection_version");

        if (error) {
          return { error: getSupabaseError(error || { message: "Error while fetching support email." }) };
        }

        return { data };
      },
    }),

    getLargeSite: builder.query<LargeSiteView | undefined, string | null | undefined>({
      providesTags: [
        { type: "Portfolio", id: "List" },
        { type: "Portfolio", id: "PortfolioExportUsage" },
      ],
      queryFn: async (id) => {
        if (!id) {
          return {
            data: undefined,
          };
        }

        const { data, error } = await supabase.from("v_large_sites").select().eq("id", id).single();

        if (error) {
          return { error: getSupabaseError(error || { message: "Error while fetching Large Sites." }) };
        }

        return { data: data as LargeSiteView };
      },
    }),

    getLargeSites: builder.query<
      {
        data: SupabaseDatabase["public"]["Views"]["v_large_sites"]["Row"][];
        count: number;
      },
      { page: number; pageSize: number; dateFrom: string; dateUntil: string }
    >({
      queryFn: async ({ page, pageSize, dateFrom, dateUntil }) => {
        const from = page * pageSize - pageSize;
        const to = page * pageSize - 1;

        const { data, error, count } = await supabase
          .from("v_large_sites")
          .select("*", { count: "exact" })
          .range(from, to)
          .filter("created_at", "gte", dateFrom)
          .filter("created_at", "lt", dateUntil);

        if (error) {
          return { error: getSupabaseError(error || { message: "Error while fetching tool logs" }) };
        }

        return {
          data: {
            data,
            count: count || 0,
          },
        };
      },
    }),

    /**
     * Updates the user's last version accessed value to the current value.
     */
    updateLastVersionAccessed: builder.mutation<string, string>({
      invalidatesTags: [],
      queryFn: async (version) => {
        const session = await supabase.auth.getSession();
        if (!session.data.session || session.error) {
          return {
            error: getSupabaseError(session.error || { message: "Session not found" }),
          };
        }
        const user = session.data.session.user;

        const payload = {
          last_version_accessed: version,
        };
        const { data, error } = await supabase
          .from("users")
          .update(payload)
          .filter("id", "eq", user.id)
          .select("last_version_accessed")
          .single();

        if (error) return { error: getSupabaseError(error) };

        const lastVersionAccessed = data.last_version_accessed;

        if (!lastVersionAccessed || lastVersionAccessed !== version) {
          return {
            error: getSupabaseError({
              message: "Failed to update last version accessed.",
            }),
          };
        }

        return {
          data: lastVersionAccessed,
        };
      },
    }),

    /**
     * Grabs the user's last version accessed value. If fails, returns null.
     */
    getLastVersionAccessed: builder.query<string | null, void>({
      queryFn: async () => {
        const session = await supabase.auth.getSession();
        if (!session.data.session || session.error) {
          return {
            error: getSupabaseError(session.error || { message: "Session not found" }),
          };
        }

        const { data } = await supabase
          .from("users")
          .select("lastVersionAccessed:last_version_accessed")
          .filter("id", "eq", session.data.session.user.id)
          .maybeSingle();

        if (data && typeof data.lastVersionAccessed !== "undefined") {
          return {
            data: data.lastVersionAccessed,
          };
        }

        return {
          data: null,
        };
      },
    }),

    getTpuLabelling: builder.query<GetTpuSubunitResponse, GetTpuSubunitRequest>({
      queryFn: async (payload) => {
        const { data, error } = await supabase.functions.invoke("get-tpu-labelling", { body: JSON.stringify(payload) });

        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),

    getEmbedPages: builder.query<Rpc["get_embed_pages"]["Returns"], void>({
      // Shares the admin list tag so editing a dataset refreshes an open viewer tab.
      providesTags: [{ type: "Dataset", id: "LIST" }],
      queryFn: async () => {
        const { data, error } = await supabase.rpc("get_embed_pages");

        return data ? { data } : { error: getSupabaseError(error) };
      },
    }),
  }),
});

export const invalidateAllSupabaseTags = () => {
  return supabaseApi.util.invalidateTags(tagTypes);
};

export const {
  useGetCurrentUserQuery,
  useLazyGetCurrentUserQuery,
  useGetUsersQuery,
  useGetPendingUserCountQuery,
  useGetPendingUsersForOrgKeyExchangeQuery,
  useSetOrgSecretsForPendingUsersMutation,
  useGetDomainsQuery,
  useGetGroupsQuery,
  useLazyGetGroupsQuery,
  useGetGroupIdQuery,
  useCreateUserMutation,
  useInviteUserByEmailMutation,
  useGetOtpMutation,
  useResendOtpMutation,
  useVerifyOtpMutation,
  useDeleteUserMutation,
  useDeleteMfaMutation,
  useRecoverUserMutation,
  useGetSlugQuery,
  useUpdateGroupMutation,
  useUpdateGroupPermissionsMutation,
  useAddGroupMutation,
  useGetGroupQuery,
  useGetCurrentGroupIdQuery,
  useDeleteGroupMutation,
  useUpdateUserGroupMutation,
  useGetAssuranceLevelQuery,
  useLazyGetAssuranceLevelQuery,
  useGetPasswordResetKeyHoldersQuery,
  useGetOrgQuery,
  useUpdateOrgMutation,
  useIsOwnerQuery,
  useGetPortfolioQuery,
  useGetPortfoliosQuery,
  useLazyGetPortfolioListPageQuery,
  useGetDistinctPortfolioUsersQuery,
  useGetToolTemplateQuery,
  useGetReportSignedUrlQuery,
  useLazyGetReportSignedUrlQuery,
  useGetExistingCollectionReportQuery,
  useGetAllPortfoliosQuery,
  useGetAllLargeSitesQuery,
  useGetErroneousPortfolioAssetsQuery,
  useLazyGetErroneousPortfolioAssetsQuery,
  useAddPortfolioMutation,
  useGetAllPortfolioAssetIdsQuery,
  useLazyGetAllPortfolioAssetIdsQuery,
  useLazyGetUnprocessedPortfolioAssetCountQuery,
  useUpdatePortfolioAssetsWithErrorsMutation,
  useUpdatePortfolioDetailsMutation,
  useUpdatePortfolioRerunMutation,
  useUpdatePortfolioStampsMutation,
  useGetUnprocessedPortfolioAssetIdsQuery,
  useLazyGetUnprocessedPortfolioAssetIdsQuery,
  useGenerateUsernameQuery,
  useLazyGenerateUsernameQuery,
  useUpdateAdaptationProgressCountsMutation,
  useUpdatePortfolioProgressCountsMutation,
  useShowMaxPortfolioModalQuery,
  useSetMaxPortfolioSizeMutation,
  useGetQuotaAllocationsQuery,
  useGetToolLogsQuery,
  useGetToolLogsCountQuery,
  useGetToolUsageQuery,
  useGetToolUsageByMonthQuery,
  useLazyGetSupportEmailQuery,
  useGetCurrentCollectionVersionQuery,
  useLazyGetCurrentCollectionVersionQuery,
  useUpdateLastVersionAccessedMutation,
  useGetLastVersionAccessedQuery,
  useGetLargeSiteQuery,
  useGetLargeSitesQuery,
  useLazyGetLargeSitesQuery,
  useGetTpuLabellingQuery,
  useLazyGetTpuLabellingQuery,
  useGetActiveAdaptationSummariesQuery,
  useGetActiveSummariesQuery,
  useGetAdaptationPathwaysWithActionsQuery,
  useLazyGetAdaptationPathwaysWithActionsQuery,
  useAddAdaptationPathwaysMutation,
  useAddAdaptationPathwayActionsMutation,
  useGetUnprocessedAdaptationPathwayActionsQuery,
  useLazyGetUnprocessedAdaptationPathwayActionsQuery,
  useUpdateAdaptationsStampsMutation,
  useGetAdaptationsQuery,
  useLazyGetAdaptationsQuery,
  useGetEmbedPagesQuery,
} = supabaseApi;
